/**
 * SalesOrderFormPage — Página completa para crear/editar Pedidos de Venta (PED-01).
 *
 * Reemplaza SalesOrderModal con layout de scroll continuo + sidebar sticky.
 * Secciones: Encabezado, Pago, Líneas de Detalle, Referencias,
 *            Campos Personalizados, Contactos de Cobranza, Adjuntos, Notas.
 *
 * Ruta: /sales/orders/new  o  /sales/orders/:id/edit
 */
import { todayISO } from '../../../lib/utils'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useCartStore } from '@/store/cartStore'
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form'
import DatePicker from '@/components/DatePicker'
import { NumericInput } from '@/components/NumericInput'
import { useTenantLocale, formatMoneyForTenant, useTenantCurrency } from '@/lib/locale'
import { totalesDelDocumento, subtotalDeLinea, subtotalDeLineaEnSuMoneda, precioEnMonedaBase } from '../lib/documentTotals'
import { inputCls } from '@/lib/formStyles'
import {
  ArrowLeft, FileText, UserCircle, List, Link2,
  StickyNote, Save, Plus, Trash2, Loader2, ClipboardList,
  Mail, Paperclip, Upload, Eye, AlertTriangle, Building2, HelpCircle, UserPlus,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Autocomplete } from '@/components/Autocomplete'
import { DataTable } from '@/components/ds/DataTable'
import type { AutocompleteItem } from '@/components/Autocomplete'
import { formatRut } from '@/lib/utils'
import {
  useCustomers, useSalesOrder,
  useCreateSalesOrder, useUpdateSalesOrder,
  useOrderAttachments, useUploadOrderAttachment, useDeleteOrderAttachment,
  uploadOrderAttachmentFile, useCreateCustomer, useDriverInfo,
} from '@/modules/sales/hooks/useSales'
import { ProductAutocomplete } from './ProductAutocomplete'
import { useProductLookup } from '../hooks/useProductLookup'
import { PendingAttachmentsPicker } from '@/modules/sales/components/PendingAttachmentsPicker'
import { CustomerModal } from '@/modules/sales/components/CustomerModal'
import { CustomerFinancialCard } from '@/modules/sales/components/three60/CustomerFinancialCard'
import type {
  CustomerListItem, ProductListItem, SaleDocumentLine,
  DocumentReferencia, DocumentPersonalizado,
} from '@/modules/sales/hooks/useSales'
import {
  useLocalizationConfig, useGlobalCustomFields, useCurrencyRates, useTenantTax,
} from '@/modules/config/hooks/useConfig'
import type { CurrencyRateInfo, GlobalCustomField } from '@/modules/config/hooks/useConfig'
import { useSellers } from '@/modules/users/hooks/useUsers'
import { usePermission } from '@/hooks/usePermission'
import { HelpPanel } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { ToastContainer, useToast } from '@/components/Toast'
import {
  aplanarErroresDeCampo, aplanarErroresDelFormulario, extractErrorMessage, extractFieldErrors, rutaLegible,
  type ErrorDeCampo,
} from '@/lib/errorUtils'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { Tooltip } from '@/components/ds/Tooltip'

// ─── Constants ───────────────────────────────────────────────────────────────

const readonlyCls = 'w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface-muted text-content-muted cursor-default'
const labelCls = 'block text-xs font-medium text-content-muted mb-1'
const cellInputCls = 'w-full px-2 py-1.5 text-xs border border-divider rounded bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200 transition-all'
const errorCls = 'mt-1 text-xs text-danger-solid'

const UNITS = ['UN', 'KG', 'MT', 'LT', 'HR', 'DY', 'MO', 'BX', 'PK', 'OT']

const REF_DOC_TYPES = [
  { group: 'DTEs', options: [
    { value: '33', label: '33 — Factura Electrónica' },
    { value: '34', label: '34 — Factura No Afecta o Exenta' },
    { value: '39', label: '39 — Boleta Electrónica' },
    { value: '41', label: '41 — Boleta No Afecta o Exenta' },
    { value: '48', label: '48 — Comprobante de Pago Electrónico' },
    { value: '52', label: '52 — Guía de Despacho' },
    { value: '56', label: '56 — Nota de Débito' },
    { value: '61', label: '61 — Nota de Crédito' },
  ]},
  { group: 'Documentos Comerciales', options: [
    { value: '801', label: '801 — Orden de Compra' },
    { value: '802', label: '802 — Nota de Pedido' },
    { value: '803', label: '803 — Contrato' },
    { value: '804', label: '804 — Resolución' },
    { value: 'HES', label: 'HES — H. Entrada de Servicios' },
  ]},
]

const EMPTY_LINE: OrderLine = {
  sku: '', name: '', description: '', unit: 'UN', quantity: '1', unit_price: '0',
  price_currency: '', discount_pct: '0', is_tax_exempt: false,
  commission_rate: '0', tax_data: {},
}

const EMPTY_REF: DocumentReferencia = {
  tipo_doc: '', folio_ref: '', fecha_ref: '', cod_ref: '', razon_ref: '',
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface OrderLine {
  sku: string
  name: string
  description: string
  unit: string
  quantity: string
  unit_price: string
  price_currency: string
  discount_pct: string
  is_tax_exempt: boolean
  commission_rate: string
  tax_data: Record<string, unknown>
}

interface GlobalCampoValue {
  id: number
  nombre: string
  tipo: 'string' | 'numero' | 'fecha' | 'select' | 'boolean'
  is_required: boolean
  valor: string
  opciones?: string[]
}

interface ContactEntry {
  email: string
}

interface OrderFormData {
  customer: string
  sales_rep: string
  order_date: string
  target_document_type: string
  payment_method: 'contado' | 'credito'
  payment_term_days: number
  expected_delivery_date: string
  notes: string
  lines: OrderLine[]
  referencias: DocumentReferencia[]
  global_campos: GlobalCampoValue[]
  personalizados: DocumentPersonalizado[]
  collection_contacts: ContactEntry[]
  lock_exchange_rate: boolean
}

// ─── Section definitions ──────────────────────────────────────────────────────

const SECTIONS: { id: string; label: string; icon: LucideIcon; color: string }[] = [
  { id: 'sec-encabezado', label: 'Encabezado', icon: FileText, color: 'bg-primary-50 text-primary-hover dark:text-primary-200' },
  { id: 'sec-detalle', label: 'Líneas de Detalle', icon: List, color: 'bg-info-surface text-info-content-soft' },
  { id: 'sec-referencias', label: 'Referencias', icon: Link2, color: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-300' },
  { id: 'sec-campos', label: 'Campos Personalizados', icon: ClipboardList, color: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300' },
  { id: 'sec-cobranza', label: 'Contactos de Cobranza', icon: Mail, color: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-300' },
  { id: 'sec-adjuntos', label: 'Adjuntos', icon: Paperclip, color: 'bg-warning-surface text-warning-content-soft' },
  { id: 'sec-notas', label: 'Notas', icon: StickyNote, color: 'bg-surface-muted text-content-muted' },
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtCLP = (v: number) =>
  formatMoneyForTenant(v)

function buildGlobalCampos(fields: GlobalCustomField[]): GlobalCampoValue[] {
  return fields.map(f => ({
    id: f.id,
    nombre: f.nombre,
    tipo: f.tipo as GlobalCampoValue['tipo'],
    is_required: f.is_required,
    valor: f.tipo === 'boolean' ? 'false' : '',
    opciones: f.opciones ?? [],
  }))
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Section({
  id, icon: Icon, title, color, children,
}: {
  id: string
  icon: LucideIcon
  title: string
  color: string
  children: React.ReactNode
}) {
  return (
    <section id={id} className="bg-glass backdrop-blur-glass rounded-card border border-divider shadow-sm overflow-hidden">
      {/* Aplicar la clase color completa (incluye dark: variants) al header bar
       * para que se adapte en ambos temas. El icon container usa surface-muted
       * para diferenciarse visualmente del header bar. */}
      <div className={`flex items-center gap-2.5 px-5 py-3.5 border-b border-divider ${color}`}>
        <div className="p-1.5 rounded-lg bg-surface/60 dark:bg-black/30">
          <Icon size={14} />
        </div>
        <h2 className="text-sm font-semibold text-content">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </section>
  )
}

function LineTooltip({ content, children }: {
  content: React.ReactNode
  children: React.ReactNode
}) {
  // El tooltip del design system (2026-09-30). Reemplaza a uno escrito a mano
  // que no respondía al teclado ni se asociaba por aria-describedby, y que
  // se ubicaba mal con la página desplazada: sumaba `window.scrollY` a una
  // posición `fixed`, que ya se mide contra la ventana. Va
  // por portal, así que no lo recorta el overflow de la tabla, y se acota solo
  // a la ventana: el `align` que tenía antes ya no hace falta.
  return (
    <Tooltip content={content} side="bottom" delay={150}>
      {/* tabIndex: sin él el teclado nunca llega al disparador y la ayuda
          queda solo para el mouse (WCAG 1.4.13: al pasar el mouse O al enfocar). */}
      <span className="inline-flex rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring" tabIndex={0}>{children}</span>
    </Tooltip>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────

// Nombres de los campos del pedido para el resumen de errores.
const CAMPOS_DEL_PEDIDO: Record<string, string> = {
  customer: 'Cliente', sales_rep: 'Vendedor', seller: 'Vendedor', order_date: 'Fecha del pedido',
  target_document_type: 'Documento a emitir', payment_method: 'Forma de pago', payment_term_days: 'Plazo',
  expected_delivery_date: 'Entrega estimada', notes: 'Notas', lines: 'Líneas', referencias: 'Referencias',
  personalizados: 'Campos personalizados', global_campos: 'Campos personalizados',
  collection_contacts: 'Contactos de cobranza', email: 'Correo', sku: 'SKU', name: 'Nombre',
  description: 'Descripción', unit: 'Unidad', quantity: 'Cantidad', unit_price: 'Precio unitario',
  price_currency: 'Moneda', discount_pct: 'Descuento', product_id: 'Producto', tax_data: 'Impuestos',
  tipo_doc: 'Tipo de documento', folio_ref: 'Folio', cod_ref: 'Código', razon_ref: 'Razón',
  nombre: 'Nombre', valor: 'Valor', tipo: 'Tipo', lock_exchange_rate: 'Fijar tipo de cambio',
}
const LISTAS_DEL_PEDIDO: Record<string, string> = {
  lines: 'Línea', referencias: 'Referencia', personalizados: 'Campo', global_campos: 'Campo',
  collection_contacts: 'Contacto',
}

function ResumenDeErrores({ errores }: { errores: ErrorDeCampo[] }) {
  if (!errores.length) return null
  return (
    <div role="alert" className="p-3 bg-danger-surface border border-danger-border rounded-xl text-xs text-danger-content-soft space-y-1">
      <p className="font-semibold">No se pudo guardar. Revisar:</p>
      <ul className="list-disc pl-4 space-y-0.5">
        {errores.map((e, i) => {
          const donde = rutaLegible(e.ruta, CAMPOS_DEL_PEDIDO, LISTAS_DEL_PEDIDO)
          return <li key={`${e.ruta}-${i}`}>{donde ? <><span className="font-medium">{donde}:</span> {e.mensaje}</> : e.mensaje}</li>
        })}
      </ul>
    </div>
  )
}

export function SalesOrderFormPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { id: editId } = useParams<{ id: string }>()
  const isEditing = !!editId

  const { can } = usePermission()
  const canAssignSeller = can('sales.orders', 'approve')

  // ── Data queries ──
  const { data: customersData } = useCustomers({ active: 'true', page_size: 9999 })
  const { data: locConfig } = useLocalizationConfig()
  const { data: globalFields = [] } = useGlobalCustomFields(true)
  const { data: currencyRatesData } = useCurrencyRates()
  const { data: existingOrder } = useSalesOrder(editId ?? null)
  const { data: sellersData } = useSellers()
  const { data: attachmentsData } = useOrderAttachments(editId ?? null)

  const tax = useTenantTax()
  const taxRate = tax.rate
  const customers: CustomerListItem[] = customersData?.customers || []
  const sellers = sellersData?.sellers ?? []
  const currencyRates = currencyRatesData?.rates as Record<string, CurrencyRateInfo> | undefined

  // ── Mutations ──
  const createOrder = useCreateSalesOrder()
  const updateOrder = useUpdateSalesOrder()
  const uploadAttachment = useUploadOrderAttachment(editId ?? '')
  const deleteAttachment = useDeleteOrderAttachment(editId ?? '')
  // Adjuntos buffereados al CREAR (sin id padre todavía) — se suben tras crear.
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [savingAttachments, setSavingAttachments] = useState(false)
  const isPending = createOrder.isPending || updateOrder.isPending || savingAttachments

  // ── Form ──
  const today = todayISO()
  const locale = useTenantLocale()
  const baseCurrency = useTenantCurrency()

  const toast = useToast()

  const {
    register, handleSubmit, control, watch, setValue, reset, setError,
    formState: { errors },
  } = useForm<OrderFormData>({
    defaultValues: {
      customer: '', sales_rep: '', order_date: today,
      target_document_type: 'FE',
      payment_method: 'contado', payment_term_days: 0,
      expected_delivery_date: '', notes: '',
      lines: [{ ...EMPTY_LINE }],
      referencias: [],
      global_campos: [],
      personalizados: [],
      collection_contacts: [] as ContactEntry[],
      lock_exchange_rate: false,
    },
  })

  const { fields: lineFields, append: appendLine, remove: removeLine } = useFieldArray({ control, name: 'lines' })

  // Líneas precargadas del "pedido abierto" (carrito) al navegar desde el CartBell
  // con state.cartLines. Se aplican en el reset de "pedido nuevo" (abajo) para que
  // NO las pise ese reset cuando cargan los campos globales.
  const cartLines = useMemo<OrderLine[]>(
    () => ((location.state as { cartLines?: OrderLine[] } | null)?.cartLines) ?? [],
    [location.state],
  )
  const { fields: refFields, append: appendRef, remove: removeRef } = useFieldArray({ control, name: 'referencias' })
  const { fields: personFields, append: appendPerson, remove: removePerson } = useFieldArray({ control, name: 'personalizados' })
  const { fields: contactFields, append: appendContact, remove: removeContact } = useFieldArray({ control, name: 'collection_contacts' })

  // ── Crear cliente inline (sin salir del pedido) ──
  const createCustomer = useCreateCustomer()
  const { data: driverInfo } = useDriverInfo()
  const taxIdLabel = driverInfo?.tax_id_label || 'RUT'
  const [customerModalOpen, setCustomerModalOpen] = useState(false)
  const [customerErrors, setCustomerErrors] = useState<Record<string, string>>()

  const handleCreateCustomer = (data: any) => {
    setCustomerErrors(undefined)
    createCustomer.mutate(data, {
      onSuccess: (created: { id?: string }) => {
        setCustomerModalOpen(false)
        // La lista de clientes se refetchea (invalidateQueries) → el Autocomplete
        // resuelve el nombre; dejamos el nuevo cliente seleccionado en el pedido.
        if (created?.id) setValue('customer', created.id, { shouldValidate: true })
        toast.success('Cliente creado y seleccionado.')
      },
      onError: (e) => {
        const fields = extractFieldErrors(e)
        if (fields) setCustomerErrors(fields)
        else toast.error('No se pudo crear el cliente.')
      },
    })
  }

  // ── Populate global campos once loaded ──
  const [globalCamposInitialized, setGlobalCamposInitialized] = useState(false)
  useEffect(() => {
    if (globalFields.length > 0 && !globalCamposInitialized && !isEditing) {
      setValue('global_campos', buildGlobalCampos(globalFields))
      setGlobalCamposInitialized(true)
    }
  }, [globalFields, globalCamposInitialized, isEditing, setValue])

  // ── Populate form when editing ──
  useEffect(() => {
    if (isEditing && existingOrder) {
      const editLines: OrderLine[] =
        existingOrder.lines && existingOrder.lines.length > 0
          ? existingOrder.lines.map((line: SaleDocumentLine) => ({
              sku: line.sku || '',
              name: line.name || '',
              description: (line as any).description || '',
              unit: line.unit || 'UN',
              quantity: line.quantity || '1',
              unit_price:
                line.unit_price_original && line.price_currency && line.price_currency !== baseCurrency
                  ? line.unit_price_original
                  : line.unit_price || '0',
              price_currency: line.price_currency || baseCurrency,
              discount_pct: line.discount_pct || '0',
              is_tax_exempt: line.is_tax_exempt ?? false,
              commission_rate: '0',
              tax_data: line.tax_data || {},
            }))
          : [{ ...EMPTY_LINE }]

      if (existingOrder.commission_data) {
        for (const cd of existingOrder.commission_data) {
          const idx = editLines.findIndex(l => l.sku === cd.sku)
          if (idx >= 0) editLines[idx].commission_rate = cd.commission_rate || '0'
        }
      }

      // Split personalizados: global ones go to global_campos, ad-hoc to personalizados
      const globalNombres = new Set(globalFields.map(f => f.nombre))
      const editGlobalCampos: GlobalCampoValue[] = buildGlobalCampos(globalFields)
      const adHocPersonalizados: DocumentPersonalizado[] = []

      for (const p of (existingOrder.personalizados || [])) {
        if (globalNombres.has(p.nombre)) {
          const gc = editGlobalCampos.find(g => g.nombre === p.nombre)
          if (gc) gc.valor = p.valor || ''
        } else {
          adHocPersonalizados.push(p)
        }
      }

      reset({
        customer: existingOrder.customer || '',
        sales_rep: (existingOrder as any).sales_rep || '',
        order_date: existingOrder.order_date || today,
        target_document_type: existingOrder.target_document_type || 'FE',
        payment_method: existingOrder.payment_method || 'contado',
        payment_term_days: existingOrder.payment_term_days || 0,
        expected_delivery_date: existingOrder.expected_delivery_date || '',
        notes: existingOrder.notes || '',
        lines: editLines,
        referencias: existingOrder.referencias || [],
        global_campos: editGlobalCampos,
        personalizados: adHocPersonalizados,
        collection_contacts: (existingOrder.collection_contacts || []).map((e: string) => ({ email: e })),
        lock_exchange_rate: (existingOrder as any).lock_exchange_rate ?? false,
      })
      setGlobalCamposInitialized(true)
    } else if (!isEditing) {
      reset({
        customer: '', sales_rep: '', order_date: today,
        target_document_type: 'FE',
        payment_method: 'contado', payment_term_days: 0,
        expected_delivery_date: '', notes: '',
        lines: cartLines.length > 0 ? cartLines : [{ ...EMPTY_LINE }],
        referencias: [],
        global_campos: globalFields.length > 0 ? buildGlobalCampos(globalFields) : [],
        personalizados: [],
        collection_contacts: [],
        lock_exchange_rate: false,
      })
      setGlobalCamposInitialized(globalFields.length > 0)
    }
  }, [isEditing, existingOrder, reset, today, globalFields, cartLines]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Watched values ──
  const watchedCustomerId = watch('customer')
  const watchedLines = useWatch({ control, name: 'lines' }) ?? []
  // Productos de las líneas ya cargadas + los elegidos en la sesión. La búsqueda
  // va al servidor (ProductAutocomplete): bajar los primeros N dejaba sin poder
  // buscar al resto del catálogo.
  const { products, remember: recordarProducto } = useProductLookup(watchedLines as any)
  const watchedPaymentMethod = watch('payment_method')
  const watchedPaymentTermDays = watch('payment_term_days')
  const watchedOrderDate = watch('order_date')
  const watchedGlobalCampos = useWatch({ control, name: 'global_campos' }) ?? []

  // ── Auto-fill payment terms from customer ──
  const selectedCustomer = useMemo(
    () => customers.find(c => c.id === watchedCustomerId) ?? null,
    [customers, watchedCustomerId],
  )

  useEffect(() => {
    if (selectedCustomer && !isEditing) {
      const cust = selectedCustomer as any
      if (cust.payment_term_days && cust.payment_term_days > 0) {
        setValue('payment_method', 'credito')
        setValue('payment_term_days', cust.payment_term_days)
      } else {
        setValue('payment_method', 'contado')
        setValue('payment_term_days', 0)
      }
    }
  }, [watchedCustomerId, selectedCustomer, isEditing, setValue])

  // ── Currency rate helpers ──
  const getRate = (currency: string): number | null => {
    if (!currency || currency === baseCurrency || !currencyRates) return null
    const info = currencyRates[currency]
    return info ? parseFloat(info.rate) : null
  }

  // ── Totals calculation ──
  // Los totales salen de `documentTotals`, que replica el orden de operaciones
  // del backend. Antes esto redondeaba el precio convertido con `Math.round`
  // —que trunca a entero cualquier moneda— y no redondeaba el subtotal de cada
  // línea, así que el pedido mostraba un total distinto del que se guardaba.
  const opcionesDeTotales = { baseCurrency, taxRate, getRate }
  const { montoNeto, montoExento, montoIVA, total, comisionTotal } = useMemo(
    () => totalesDelDocumento(watchedLines as any, opcionesDeTotales),
    [watchedLines, currencyRates, baseCurrency, taxRate], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const foreignCurrency = watchedLines
    .map(l => (l as any).price_currency)
    .find(c => c && c !== baseCurrency) || null

  // ── Product autocomplete items ──
  const customerItems: AutocompleteItem[] = useMemo(
    () => customers.map(c => ({
      id: c.id, label: c.legal_name,
      sublabel: c.tax_id ? formatRut(c.tax_id) : undefined,
    })),
    [customers],
  )

  // ── Fill line from product ──
  const fillProduct = (index: number, product: ProductListItem) => {
    setValue(`lines.${index}.sku`, product.sku)
    setValue(`lines.${index}.name`, product.name)
    setValue(`lines.${index}.unit`, product.unit)
    setValue(`lines.${index}.unit_price`, product.unit_price)
    setValue(`lines.${index}.is_tax_exempt`, product.is_tax_exempt)
    setValue(`lines.${index}.price_currency`, product.price_currency || baseCurrency)
    setValue(`lines.${index}.commission_rate`, product.commission_rate || '0')
  }

  // Precios disponibles de un producto: el por defecto + los adicionales por
  // moneda. Habilita el selector de moneda por línea (#multi-precio).
  const productPriceOptions = (product?: ProductListItem) => {
    if (!product) return [] as { currency: string; unit_price: string }[]
    const opts = [{ currency: product.price_currency || baseCurrency, unit_price: product.unit_price }]
    for (const p of product.prices ?? []) opts.push({ currency: p.currency, unit_price: p.unit_price })
    return opts
  }

  // OrderLine no guarda product_id → se matchea por SKU (es único).
  const findLineProduct = (line: any): ProductListItem | undefined =>
    line?.sku ? products.find(p => p.sku === line.sku) : undefined

  const changeLineCurrency = (index: number, product: ProductListItem, currency: string) => {
    const opt = productPriceOptions(product).find(o => o.currency === currency)
    setValue(`lines.${index}.price_currency`, currency)
    if (opt) setValue(`lines.${index}.unit_price`, opt.unit_price)
  }

  // ── Due date display ──
  const dueDate = useMemo(() => {
    if (watchedPaymentMethod !== 'credito' || !watchedPaymentTermDays || !watchedOrderDate) return null
    const d = new Date(watchedOrderDate + 'T12:00:00')
    d.setDate(d.getDate() + watchedPaymentTermDays)
    return d.toLocaleDateString('es-CL')
  }, [watchedPaymentMethod, watchedPaymentTermDays, watchedOrderDate])

  // ── Scroll spy ──
  const [activeSection, setActiveSection] = useState('sec-encabezado')
  const [helpOpen, setHelpOpen] = useState(false)
  /**
   * Las filas de la tabla de líneas: identidad, POSICIÓN y lo que se deriva de
   * ella. Se arman acá y no en el `render` por dos razones distintas:
   *
   * 🔴 El índice tiene que viajar DENTRO del dato. Los campos del formulario se
   * nombran por posición (`lines.3.sku`) y el `render` de la tabla recibe el
   * índice de la fila YA ORDENADA, así que con el índice del render el día que
   * alguien marque una columna `sortable` cada campo escribiría en otra línea,
   * sin ningún error.
   *
   * ⚠️ Y lo derivado —la moneda, el subtotal en las dos monedas— lo usan varias
   * columnas: calcularlo por columna lo repetiría cuatro veces por fila.
   *
   * ⚠️ La fuente de la LONGITUD es `lineFields` y no `watchedLines`: el primero
   * crece en el mismo render en que se agrega la línea y el segundo recién en el
   * siguiente, así que ahí el índice nuevo llega `undefined`.
   *
   * ⚠️ Y NO va en un `useMemo`, a propósito: sus tres entradas —`watchedLines`,
   * `getRate` y `opcionesDeTotales`— se recrean en cada render, así que
   * memoizarlo recalcularía igual y solo agregaría la ilusión de que no.
   */
  const filasDeLinea = lineFields.map((field, idx) => {
    const line = (watchedLines[idx] ?? {}) as any
    const cur = line?.price_currency || baseCurrency
    return {
      id: field.id,
      idx,
      line,
      cur,
      rate: getRate(cur),
      // ⚠️ Convierte el PRECIO y después multiplica, que es el orden del
      // backend. Convertir el subtotal ya multiplicado da otro número apenas
      // la cantidad es mayor que uno.
      subtotalBase: subtotalDeLinea(line, opcionesDeTotales),
      subtotalPropio: subtotalDeLineaEnSuMoneda(line, opcionesDeTotales),
      desc: (line?.description ?? '') as string,
    }
  })

  const [expandedLines, setExpandedLines] = useState<Set<number>>(new Set())
  const toggleLine = (idx: number) => {
    setExpandedLines(prev => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }
  const scrollContainerRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    const firstSection = document.getElementById('sec-encabezado')
    let container: HTMLElement | null = firstSection?.parentElement ?? null
    while (container && container !== document.documentElement) {
      const style = getComputedStyle(container)
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') break
      container = container.parentElement
    }
    const target = container || window
    scrollContainerRef.current = container

    const handleScroll = () => {
      for (const s of [...SECTIONS].reverse()) {
        const el = document.getElementById(s.id)
        if (el) {
          const rect = el.getBoundingClientRect()
          if (rect.top <= 140) { setActiveSection(s.id); return }
        }
      }
    }
    target.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => target.removeEventListener('scroll', handleScroll)
  }, [isEditing])

  // ── File upload handler ──
  const [uploadError, setUploadError] = useState<string | null>(null)
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadError(null)
    try {
      await uploadAttachment.mutateAsync(file)
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || 'Error al subir el archivo.'
      setUploadError(msg)
    }
    // Reset input
    e.target.value = ''
  }

  // ── Submit ──
  // Los errores del servidor se listan aparte, con su ubicación: los de las
  // líneas llegan anidados y no tienen un campo del formulario donde mostrarse.
  const [erroresDelServidor, setErroresDelServidor] = useState<ErrorDeCampo[]>([])

  const onSubmit = (data: OrderFormData) => {
    setErroresDelServidor([])
    // Merge global_campos into personalizados
    const globalMerged = (data.global_campos || [])
      .filter(c => c.valor !== '' || c.is_required || c.tipo === 'boolean')
      .map(({ nombre, tipo, valor }) => ({ nombre, tipo, valor }))
    const allPersonalizados = [...globalMerged, ...(data.personalizados || [])]

    const payload = {
      customer: data.customer,
      ...(data.sales_rep ? { sales_rep: data.sales_rep } : {}),
      order_date: data.order_date,
      target_document_type: data.target_document_type,
      payment_method: data.payment_method,
      payment_term_days: data.payment_method === 'credito' ? data.payment_term_days : 0,
      expected_delivery_date: data.expected_delivery_date || null,
      notes: data.notes,
      referencias: data.referencias,
      personalizados: allPersonalizados,
      collection_contacts: data.collection_contacts.map(c => c.email).filter(Boolean),
      lock_exchange_rate: data.lock_exchange_rate,
      lines: data.lines.map(line => ({
        sku: line.sku,
        name: line.name,
        description: line.description || '',
        unit: line.unit,
        quantity: line.quantity,
        unit_price: line.unit_price,
        price_currency: line.price_currency,
        discount_pct: line.discount_pct,
        is_tax_exempt: line.is_tax_exempt,
        tax_data: line.tax_data,
      })),
    }

    const onError = (e: unknown) => {
      setErroresDelServidor(aplanarErroresDeCampo(e))
      const fieldErrors = extractFieldErrors(e)
      if (fieldErrors) {
        for (const [field, message] of Object.entries(fieldErrors)) {
          // DRF devuelve errores anidados de líneas como "lines" → mostrar como
          // mensaje general; los simples (top-level) se mapean a setError de RHF.
          try {
            setError(field as keyof OrderFormData, { message })
          } catch {
            /* campo no controlado por el form — ignorar y dejar el toast */
          }
        }
      }
      toast.error(extractErrorMessage(e, 'No se pudo guardar el pedido. Revisa los campos marcados.'))
    }

    if (isEditing && editId) {
      updateOrder.mutate(
        { id: editId, data: payload },
        { onSuccess: () => navigate('/sales/orders'), onError },
      )
    } else {
      createOrder.mutate(payload, {
        onSuccess: async (created: { id?: string }) => {
          // El pedido se guardó → vaciar el carrito si venía del "pedido abierto".
          if (cartLines.length > 0) useCartStore.getState().clear()
          // Subir los adjuntos buffereados ahora que el pedido tiene id.
          if (created?.id && pendingFiles.length > 0) {
            setSavingAttachments(true)
            const failed: string[] = []
            for (const file of pendingFiles) {
              try {
                await uploadOrderAttachmentFile(created.id, file)
              } catch {
                failed.push(file.name)
              }
            }
            setSavingAttachments(false)
            // Si alguna subida falló, llevar a la edición del pedido creado para
            // que el usuario vea lo que sí se adjuntó y re-suba lo demás.
            if (failed.length > 0) {
              navigate(`/sales/orders/${created.id}/edit`)
              return
            }
          }
          navigate('/sales/orders')
        },
        onError,
      })
    }
  }

  // ─── RENDER ────────────────────────────────────────────────────────────────

  const visibleSections = SECTIONS

  return (
    <div data-tour="orderform-page" className="min-h-screen bg-surface-muted">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-surface border-b border-divider shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/sales/orders')}
              className="flex items-center gap-1.5 text-xs text-content-muted hover:text-primary transition-colors group"
            >
              <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
              Volver
            </button>
            <div className="w-px h-5 bg-surface-muted" />
            <span className="text-xs font-medium text-content">
              {isEditing ? `Editar pedido ${existingOrder?.order_number ?? ''}`.trim() : 'Nuevo pedido'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-warning-surface text-warning-content border border-warning-border">
              Borrador
            </span>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="relative p-1.5 rounded-lg text-content-subtle hover:text-primary hover:bg-primary-50 transition-colors"
              title="Ayuda sobre este formulario"
            >
              <HelpCircle size={16} />
              <span className="absolute inset-0 rounded-lg border-2 border-primary/40 animate-help-ping" />
            </button>
          </div>
        </div>
      </header>

      <HelpPanel
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title={HELP['sales.orders.form'].title}
        sections={HELP['sales.orders.form'].sections}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex gap-6">

          {/* ═══ LEFT: FORM SECTIONS ═══ */}
          <main className="flex-1 min-w-0 space-y-5 pb-20">

            {/* ── Encabezado ── */}
            <Section id="sec-encabezado" icon={FileText} title="Encabezado del Pedido" color="bg-primary-50 text-primary">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
                {/* Cliente */}
                <div data-tour="orderform-customer" className="lg:col-span-2">
                  <div className="flex items-center justify-between">
                    <label className={labelCls}>Cliente <span className="text-danger-solid">*</span></label>
                    <button type="button"
                      onClick={() => { setCustomerErrors(undefined); setCustomerModalOpen(true) }}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary-hover transition-colors">
                      <UserPlus size={13} />Nuevo cliente
                    </button>
                  </div>
                  <Controller name="customer" control={control} rules={{ required: 'Requerido' }}
                    render={({ field }) => (
                      <Autocomplete aria-label="Cliente"
                        items={customerItems}
                        value={field.value}
                        onChange={field.onChange}
                        onSelect={item => field.onChange(item.id)}
                        placeholder="Buscar cliente..."
                        className={inputCls}
                      />
                    )}
                  />
                  {errors.customer && <p className={errorCls}>{errors.customer.message}</p>}
                </div>

                {/* Tipo de Documento */}
                <SelectField
                  dense
                  label="Tipo de Documento"
                  required
                  selectProps={register('target_document_type')}
                >
                  <option value="FE">Factura Electrónica</option>
                  <option value="BE">Boleta Electrónica</option>
                </SelectField>

                {/* Vendedor (solo si puede asignar) */}
                {canAssignSeller && (
                  <SelectField
                    dense
                    label="Vendedor"
                    selectProps={register('sales_rep')}
                  >
                    <option value="">— Usuario actual —</option>
                    {sellers.map(s => (
                    <option key={s.id} value={s.id}>
                    {s.name || s.full_name}{!s.has_system_access ? ' (sin acceso)' : ''}
                    </option>
                    ))}
                  </SelectField>
                )}

                {/* Fecha de Pedido */}
                <div>
                  <label htmlFor="salesorderformpage-order-date" className={labelCls}>Fecha del Pedido <span className="text-danger-solid">*</span></label>
                  <Controller name="order_date" control={control}
                    render={({ field }) => (
                      <DatePicker id="salesorderformpage-order-date" value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                    )}
                  />
                </div>

                {/* Fecha Entrega Estimada */}
                <div>
                  <label htmlFor="salesorderformpage-expected-delivery-date" className={labelCls}>Fecha de Entrega Estimada</label>
                  <Controller name="expected_delivery_date" control={control}
                    render={({ field }) => (
                      <DatePicker id="salesorderformpage-expected-delivery-date"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="Opcional"
                      />
                    )}
                  />
                </div>

                {/* Forma de Pago */}
                <SelectField
                  dense
                  label="Forma de Pago"
                  required
                  selectProps={register('payment_method')}
                >
                  <option value="contado">Contado</option>
                  <option value="credito">Crédito</option>
                </SelectField>
                {watchedPaymentMethod === 'credito' && (
                  <div>
                    <label htmlFor="salesorderformpage-payment-term-days" className={labelCls}>Plazo (días) <span className="text-danger-solid">*</span></label>
                    <input id="salesorderformpage-payment-term-days"
                      type="number"
                      min={1}
                      {...register('payment_term_days', { valueAsNumber: true })}
                      className={inputCls}
                    />
                    {dueDate && (
                      <p className="mt-1 text-xs text-content-muted">Vencimiento estimado: <span className="font-medium">{dueDate}</span></p>
                    )}
                  </div>
                )}
              </div>

              {/* Receptor display */}
              {selectedCustomer && (
                <div className="mt-4 p-3 bg-glass-3 rounded-lg border border-divider flex flex-wrap gap-x-6 gap-y-1">
                  <div className="flex items-center gap-1.5">
                    <UserCircle size={13} className="text-content-subtle" />
                    <span className="text-xs text-content-muted">
                      <span className="font-medium text-content">{selectedCustomer.legal_name}</span>
                      {' · '}
                      {selectedCustomer.tax_id ? formatRut(selectedCustomer.tax_id) : '—'}
                    </span>
                  </div>
                  {selectedCustomer.address_street && (
                    <span className="text-xs text-content-subtle">{selectedCustomer.address_street}, {selectedCustomer.address_commune}</span>
                  )}
                </div>
              )}
            </Section>

            {/* ── Líneas de Detalle ── */}
            <div data-tour="orderform-lines">
            <Section id="sec-detalle" icon={List} title="Líneas de Detalle" color="bg-info-surface text-info-content-soft">
              {foreignCurrency && (
                <div className="mb-3 space-y-2">
                  <div className="flex items-center gap-2 px-3 py-2 bg-warning-surface border border-warning-border rounded-lg text-xs text-warning-content">
                    <AlertTriangle size={13} />
                    <span>Hay líneas en <strong>{foreignCurrency}</strong>. Los precios se convierten a CLP al guardar.</span>
                  </div>
                  <label className="flex items-start gap-2 px-3 py-2 bg-soft border border-divider rounded-lg text-xs text-content cursor-pointer">
                    <input type="checkbox" {...register('lock_exchange_rate')} className="mt-0.5 rounded border-divider-strong" />
                    <span>
                      <strong>Fijar tipo de cambio del pedido.</strong> Al facturar, se usan los montos en pesos
                      calculados con esta tasa (no se actualizan a la tasa del día de emisión).
                    </span>
                  </label>
                </div>
              )}
              <div className="-mx-5">
                <DataTable<(typeof filasDeLinea)[number]>
                  columns={[
                    { key: 'n', header: '#', width: 32,
                      render: f => <span className="text-content-subtle">{f.idx + 1}</span> },
                    { key: 'sku', header: 'SKU', width: 112,
                      render: f => (
                        <Controller name={`lines.${f.idx}.sku`} control={control}
                          render={({ field: c }) => (
                            <ProductAutocomplete field="sku" value={c.value}
                              onChange={c.onChange}
                              onSelectProduct={(prod) => { recordarProducto(prod); fillProduct(f.idx, prod) }}
                              placeholder="SKU" className={cellInputCls}
                              aria-label={`SKU de la línea ${f.idx + 1}`}
                            />
                          )}
                        />
                      ) },
                    { key: 'name', header: 'Descripción',
                      render: f => (
                        <Controller name={`lines.${f.idx}.name`} control={control}
                          render={({ field: c }) => (
                            <ProductAutocomplete field="name" value={c.value}
                              onChange={c.onChange}
                              onSelectProduct={(prod) => { recordarProducto(prod); fillProduct(f.idx, prod) }}
                              placeholder="Nombre del ítem" className={cellInputCls}
                              aria-label={`Descripción de la línea ${f.idx + 1}`}
                            />
                          )}
                        />
                      ) },
                    { key: 'quantity', header: 'Cantidad', numeric: true, width: 80,
                      render: f => (
                        <Controller name={`lines.${f.idx}.quantity`} control={control}
                          render={({ field }) => (
                            <NumericInput {...field} locale={locale} decimals={2}
                              aria-label={`Cantidad de la línea ${f.idx + 1}`}
                              className={`${cellInputCls} text-right`} />
                          )}
                        />
                      ) },
                    { key: 'unit', header: 'Unidad', width: 64,
                      render: f => (
                        <select {...register(`lines.${f.idx}.unit`)}
                          aria-label={`Unidad de la línea ${f.idx + 1}`}
                          className={`${cellInputCls} min-w-[52px]`}>
                          {UNITS.map(u => <option key={u}>{u}</option>)}
                        </select>
                      ) },
                    { key: 'unit_price', header: 'Precio Unit.', numeric: true, width: 112,
                      render: f => (
                        <div className="flex items-center gap-1.5">
                          {(() => {
                            const lineProd = findLineProduct(f.line)
                            const priceOpts = productPriceOptions(lineProd)
                            if (lineProd && priceOpts.length > 1) {
                              return (
                                <select
                                  value={f.cur}
                                  onChange={(e) => changeLineCurrency(f.idx, lineProd, e.target.value)}
                                  title="Moneda del precio"
                                  aria-label={`Moneda del precio de la línea ${f.idx + 1}`}
                                  className="text-[9px] font-bold bg-primary-100 text-primary-hover rounded px-1 py-0.5 border-0 cursor-pointer focus:ring-1 focus:ring-primary"
                                >
                                  {priceOpts.map(o => <option key={o.currency} value={o.currency}>{o.currency}</option>)}
                                </select>
                              )
                            }
                            return f.cur !== baseCurrency ? (
                              <LineTooltip
                                content={
                                  <>
                                    <p className="text-[11px] font-semibold">{fmtCLP(precioEnMonedaBase(f.line as any, opcionesDeTotales))} por unidad</p>
                                    {f.rate && <p className="text-[10px] text-content-muted mt-0.5">1 {f.cur} = {fmtCLP(f.rate)}</p>}
                                  </>
                                }
                              >
                                <span className="text-[9px] font-bold bg-primary-100 text-primary-hover rounded px-1 py-0.5 whitespace-nowrap cursor-help select-none">
                                  {f.cur}
                                </span>
                              </LineTooltip>
                            ) : null
                          })()}
                          <div className="flex-1">
                            <Controller name={`lines.${f.idx}.unit_price`} control={control}
                              render={({ field }) => (
                                <NumericInput {...field} locale={locale} decimals={3}
                                  aria-label={`Precio unitario de la línea ${f.idx + 1}`}
                                  className={`${cellInputCls} text-right`} />
                              )}
                            />
                          </div>
                        </div>
                      ) },
                    { key: 'discount_pct', header: 'Desc %', numeric: true, width: 64,
                      render: f => (
                        <Controller name={`lines.${f.idx}.discount_pct`} control={control}
                          render={({ field }) => (
                            <NumericInput {...field} locale={locale} decimals={2}
                              aria-label={`Descuento porcentual de la línea ${f.idx + 1}`}
                              className={`${cellInputCls} text-right`} />
                          )}
                        />
                      ) },
                    { key: 'subtotal', header: 'Subtotal', numeric: true, width: 96,
                      render: f => (
                        <span className="font-medium text-content">
                          {f.cur !== baseCurrency && f.rate ? (
                            <LineTooltip
                              content={
                                <>
                                  <p className="text-[11px] font-semibold">{fmtCLP(f.subtotalBase)}</p>
                                  <p className="text-[10px] text-content-muted">{f.subtotalPropio.toFixed(2)} {f.cur}</p>
                                </>
                              }
                            >
                              <span className="cursor-help underline decoration-dotted">
                                {fmtCLP(f.subtotalBase)}
                              </span>
                            </LineTooltip>
                          ) : (
                            fmtCLP(f.subtotalBase)
                          )}
                        </span>
                      ) },
                    { key: 'is_tax_exempt', header: 'Exento', width: 64,
                      render: f => (
                        <div className="text-center">
                          <input type="checkbox" {...register(`lines.${f.idx}.is_tax_exempt`)}
                            aria-label={`Exento de impuesto — línea ${f.idx + 1}`}
                            className="h-3.5 w-3.5 rounded border-divider-strong text-primary accent-primary cursor-pointer" />
                        </div>
                      ) },
                    { key: 'acciones', header: '', width: 40,
                      render: f => (
                        <div className="flex flex-col items-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => toggleLine(f.idx)}
                            title="Descripción adicional"
                            aria-expanded={expandedLines.has(f.idx)}
                            aria-label={`Descripción adicional de la línea ${f.idx + 1}`}
                            className={`p-1 rounded transition-colors ${
                              expandedLines.has(f.idx) || f.desc.length > 0
                                ? 'text-info-content-soft hover:bg-info-surface'
                                : 'text-content-subtle hover:text-info-solid hover:bg-info-surface'
                            }`}
                          >
                            <Building2 size={13} />
                          </button>
                          {lineFields.length > 1 && (
                            <button type="button" onClick={() => removeLine(f.idx)}
                              aria-label={`Eliminar la línea ${f.idx + 1}`}
                              className="p-1 rounded hover:bg-danger-surface text-content-subtle hover:text-danger-solid transition-colors">
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      ) },
                  ]}
                  rows={filasDeLinea}
                  rowKey={f => f.id}
                  minWidth={880}
                  surface={false}
                  rowExpanded={f => expandedLines.has(f.idx)}
                  expandedRow={f => (
                    <div className="px-5 py-3 border-b border-info-border bg-violet-50/40 dark:bg-violet-900/20">
                      <p className="text-[10px] font-semibold text-info-solid uppercase tracking-wider mb-1.5">
                        Descripción adicional de línea
                      </p>
                      <input
                        {...register(`lines.${f.idx}.description`)}
                        aria-label={`Descripción adicional de la línea ${f.idx + 1}`}
                        className="w-full max-w-lg px-2 py-1.5 text-xs border border-divider rounded bg-surface focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 placeholder:text-content-subtle transition-all"
                        placeholder="Detalle adicional que aparece en el documento…"
                      />
                    </div>
                  )}
                  caption="Líneas del pedido de venta"
                />
              </div>
              <div className="mt-3 px-5">
                <button type="button" onClick={() => appendLine({ ...EMPTY_LINE })}
                  className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary-hover font-medium transition-colors">
                  <Plus size={14} /> Agregar línea
                </button>
              </div>
            </Section>
            </div>

            {/* ── Referencias ── */}
            <Section id="sec-referencias" icon={Link2} title="Referencias de Documentos" color="bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-300">
              {refFields.length === 0 ? (
                <p className="text-xs text-content-subtle italic">Sin referencias asociadas.</p>
              ) : (
                <div className="space-y-3">
                  {refFields.map((field, idx) => (
                    <div key={field.id} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 p-3 bg-glass-3 rounded-lg border border-divider">
                      <SelectField
                        dense
                        label="Tipo Doc."
                        selectProps={register(`referencias.${idx}.tipo_doc`)}
                      >
                        <option value="">—</option>
                        {REF_DOC_TYPES.map(group => (
                        <optgroup key={group.group} label={group.group}>
                        {group.options.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                        </optgroup>
                        ))}
                      </SelectField>
                      <TextField
                        dense
                        label="Folio / N°"
                        placeholder="Ej: OC-001"
                        inputProps={register(`referencias.${idx}.folio_ref`)}
                      />
                      <div>
                        <label className={labelCls}>Fecha</label>
                        <Controller name={`referencias.${idx}.fecha_ref`} control={control}
                          render={({ field }) => (
                            <DatePicker aria-label="Fecha" value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                          )}
                        />
                      </div>
                      <SelectField
                        dense
                        label="Código Ref."
                        selectProps={register(`referencias.${idx}.cod_ref`)}
                      >
                        <option value="">—</option>
                        <option value="1">1 — Anula DTE</option>
                        <option value="2">2 — Corrige texto DTE</option>
                        <option value="3">3 — Corrige montos</option>
                      </SelectField>
                      <div className="flex gap-2 items-end">
                        <TextField
                          dense
                          className="flex-1"
                          label="Razón"
                          placeholder="Opcional"
                          inputProps={register(`referencias.${idx}.razon_ref`)}
                        />
                        <button type="button" onClick={() => removeRef(idx)}
                          className="mb-px p-1.5 text-content-subtle hover:text-danger-solid transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button type="button" onClick={() => appendRef({ ...EMPTY_REF })}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary-hover font-medium transition-colors">
                <Plus size={14} /> Agregar referencia
              </button>
            </Section>

            {/* ── Campos Personalizados ── */}
            <Section id="sec-campos" icon={ClipboardList} title="Campos Personalizados" color="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300">
              {/* Global custom fields */}
              {watchedGlobalCampos.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-semibold text-content-muted uppercase tracking-wider mb-3">Campos globales</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
                    {watchedGlobalCampos.map((campo, idx) => (
                      <div key={campo.id}>
                        <label className={labelCls}>
                          {campo.nombre}
                          {campo.is_required && <span className="text-danger-solid ml-1">*</span>}
                        </label>
                        {campo.tipo === 'boolean' ? (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox"
                              checked={watchedGlobalCampos[idx]?.valor === 'true'}
                              onChange={e => setValue(`global_campos.${idx}.valor`, e.target.checked ? 'true' : 'false')}
                              className="h-4 w-4 rounded border-divider-strong text-primary accent-primary"
                            />
                            <span className="text-xs text-content-muted">Activado</span>
                          </label>
                        ) : campo.tipo === 'select' && campo.opciones?.length ? (
                          <select aria-label={campo.nombre}
                            value={watchedGlobalCampos[idx]?.valor || ''}
                            onChange={e => setValue(`global_campos.${idx}.valor`, e.target.value)}
                            className={inputCls}
                          >
                            <option value="">— Seleccionar —</option>
                            {campo.opciones.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        ) : campo.tipo === 'fecha' ? (
                          <DatePicker aria-label={campo.nombre}
                            value={watchedGlobalCampos[idx]?.valor || ''}
                            onChange={v => setValue(`global_campos.${idx}.valor`, v)}
                          />
                        ) : (
                          <input aria-label={campo.nombre}
                            type={campo.tipo === 'numero' ? 'number' : 'text'}
                            value={watchedGlobalCampos[idx]?.valor || ''}
                            onChange={e => setValue(`global_campos.${idx}.valor`, e.target.value)}
                            className={inputCls}
                            placeholder={campo.tipo === 'numero' ? '0' : ''}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ad-hoc personalizados */}
              <p className="text-xs font-semibold text-content-muted uppercase tracking-wider mb-3">Campos ad-hoc</p>
              {personFields.length === 0 ? (
                <p className="text-xs text-content-subtle italic mb-3">Sin campos adicionales.</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {personFields.map((field, idx) => (
                    <div key={field.id} className="grid grid-cols-3 gap-3 p-3 bg-glass-3 rounded-lg border border-divider items-end">
                      <TextField
                        dense
                        label="Nombre"
                        placeholder="Ej: Centro"
                        inputProps={register(`personalizados.${idx}.nombre`)}
                      />
                      <TextField
                        dense
                        label="Valor"
                        inputProps={register(`personalizados.${idx}.valor`)}
                      />
                      <div className="flex items-end gap-2">
                        <SelectField
                          dense
                          className="flex-1"
                          label="Tipo"
                          selectProps={register(`personalizados.${idx}.tipo`)}
                        >
                          <option value="string">Texto</option>
                          <option value="numero">Número</option>
                          <option value="fecha">Fecha</option>
                        </SelectField>
                        <button type="button" onClick={() => removePerson(idx)}
                          className="mb-px p-1.5 text-content-subtle hover:text-danger-solid transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => appendPerson({ nombre: '', tipo: 'string', valor: '' })}
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary-hover font-medium transition-colors"
              >
                <Plus size={14} /> Agregar campo
              </button>
            </Section>

            {/* ── Contactos de Cobranza ── */}
            <div data-tour="orderform-payment">
            <Section id="sec-cobranza" icon={Mail} title="Contactos de Cobranza" color="bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-300">
              <p className="text-xs text-content-muted mb-3">
                Emails que recibirán recordatorios de pago y a los que se pre-llenarán al enviar el PDF del documento generado.
              </p>
              {contactFields.length === 0 ? (
                <p className="text-xs text-content-subtle italic mb-3">Sin contactos de cobranza.</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {contactFields.map((field, idx) => (
                    <div key={field.id} className="flex items-center gap-2">
                      <input aria-label={`Correo del contacto ${idx + 1}`}
                        {...register(`collection_contacts.${idx}.email`, {
                          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' },
                        })}
                        type="email"
                        className={inputCls}
                        placeholder="email@empresa.cl"
                      />
                      <button type="button" onClick={() => removeContact(idx)}
                        className="flex-shrink-0 p-1.5 text-content-subtle hover:text-danger-solid transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => appendContact({ email: '' })}
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary-hover font-medium transition-colors"
              >
                <Plus size={14} /> Agregar email
              </button>
            </Section>
            </div>

            {/* ── Adjuntos (solo en edición) ── */}
            <Section id="sec-adjuntos" icon={Paperclip} title="Archivos Adjuntos" color="bg-warning-surface text-warning-content-soft">
              {!isEditing ? (
                <PendingAttachmentsPicker files={pendingFiles} onChange={setPendingFiles} />
              ) : (
                <>
                <p className="text-xs text-content-muted mb-3">
                  Máx. 20 MB total. Tipos permitidos: PDF, JPEG, PNG, WEBP.
                </p>

                {/* Upload button */}
                <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border border-dashed border-divider-strong hover:border-primary hover:bg-primary-50 transition-colors text-xs text-content-muted hover:text-primary">
                  <Upload size={14} />
                  {uploadAttachment.isPending ? (
                    <span className="flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Subiendo...</span>
                  ) : (
                    'Subir archivo'
                  )}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={handleFileUpload}
                    disabled={uploadAttachment.isPending}
                    className="hidden"
                  />
                </label>

                {uploadError && (
                  <p className="mt-2 text-xs text-danger-solid">{uploadError}</p>
                )}

                {/* Attachment list */}
                {attachmentsData && attachmentsData.count > 0 ? (
                  <div className="mt-4 space-y-2">
                    {attachmentsData.attachments.map(att => (
                      <div key={att.id} className="flex items-center gap-3 p-2.5 bg-glass-3 rounded-lg border border-divider group">
                        <Paperclip size={14} className="text-content-subtle flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-content truncate">{att.file_name}</p>
                          <p className="text-[10px] text-content-subtle">
                            {(att.file_size / 1024).toFixed(1)} KB
                            {att.uploaded_by_name && ` · ${att.uploaded_by_name}`}
                          </p>
                        </div>
                        <a
                          href={att.download_url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 text-content-subtle hover:text-primary transition-colors"
                          title="Ver"
                        >
                          <Eye size={13} />
                        </a>
                        <button
                          type="button"
                          onClick={() => deleteAttachment.mutate(att.id)}
                          disabled={deleteAttachment.isPending}
                          className="p-1.5 text-content-subtle hover:text-danger-solid transition-colors opacity-0 group-hover:opacity-100"
                          title="Eliminar"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  attachmentsData && (
                    <p className="mt-3 text-xs text-content-subtle italic">Sin archivos adjuntos.</p>
                  )
                )}
                </>
              )}
            </Section>

            {/* ── Notas ── */}
            <Section id="sec-notas" icon={StickyNote} title="Notas Internas" color="bg-surface-muted text-content-muted">
              <textarea aria-label="Notas internas"
                {...register('notes')}
                rows={4}
                className={`${inputCls} resize-none`}
                placeholder="Observaciones o instrucciones especiales para este pedido..."
              />
            </Section>
          </main>

          {/* ═══ RIGHT: SIDEBAR ═══ */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-20 space-y-4">

              {/* Situación financiera del cliente (Form 360, Fase 4b/5) */}
              <CustomerFinancialCard customerId={watchedCustomerId} />

              {/* Nav */}
              <nav className="bg-glass backdrop-blur-glass rounded-card border border-divider shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-divider bg-surface-muted">
                  <p className="text-[11px] font-semibold text-content-muted uppercase tracking-wider">Secciones</p>
                </div>
                <ul className="py-1">
                  {visibleSections.map(s => {
                    const Icon = s.icon
                    const isActive = activeSection === s.id
                    return (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => {
                            document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                          }}
                          className={`w-full flex items-center gap-2.5 px-4 py-2 text-left transition-colors text-xs ${
                            isActive
                              ? 'bg-primary-50 text-primary font-medium'
                              : 'text-content-muted hover:bg-soft hover:text-content'
                          }`}
                        >
                          <Icon size={13} />
                          {s.label}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </nav>

              {/* Totals */}
              <div className="bg-glass backdrop-blur-glass rounded-card border border-divider shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-divider bg-surface-muted">
                  <p className="text-[11px] font-semibold text-content-muted uppercase tracking-wider">Resumen</p>
                </div>
                <div className="p-4 space-y-2 text-xs">
                  {montoNeto > 0 && (
                    <div className="flex justify-between">
                      <span className="text-content-muted">Subtotal neto</span>
                      <span className="font-medium text-content tabular-nums">{fmtCLP(montoNeto)}</span>
                    </div>
                  )}
                  {montoExento > 0 && (
                    <div className="flex justify-between">
                      <span className="text-content-muted">Exento</span>
                      <span className="font-medium text-content tabular-nums">{fmtCLP(montoExento)}</span>
                    </div>
                  )}
                  {montoIVA > 0 && (
                    <div className="flex justify-between">
                      <span className="text-content-muted">{tax.name} ({taxRate}%)</span>
                      <span className="font-medium text-content tabular-nums">{fmtCLP(montoIVA)}</span>
                    </div>
                  )}
                  <div className="pt-2 border-t border-divider flex justify-between">
                    <span className="font-semibold text-content">Total</span>
                    <span className="font-bold text-content tabular-nums text-sm">{fmtCLP(total)}</span>
                  </div>
                  {comisionTotal > 0 && (
                    <div className="flex justify-between text-rose-600 dark:text-rose-300">
                      <span>Comisión</span>
                      <span className="font-medium tabular-nums">{fmtCLP(comisionTotal)}</span>
                    </div>
                  )}
                  {foreignCurrency && currencyRates?.[foreignCurrency] && (
                    <div className="border-t border-divider pt-2 mt-1 space-y-0.5">
                      <div className="flex justify-between text-[10px] text-content-muted">
                        <span className="font-semibold text-primary">1 {foreignCurrency}</span>
                        <span>
                          ${parseFloat(currencyRates[foreignCurrency].rate).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          {' '}({currencyRates[foreignCurrency].rate_date})
                        </span>
                      </div>
                      {parseFloat(currencyRates[foreignCurrency].rate) > 0 && (
                        <div className="flex justify-between">
                          <span className="font-semibold text-primary">Total {foreignCurrency}</span>
                          <span className="font-bold text-content tabular-nums">
                            {(total / parseFloat(currencyRates[foreignCurrency].rate)).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      )}
                      <p className="text-[10px] text-warning-content-soft">
                        Totales en CLP · tasa indicativa
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Save button */}
              <button
                type="submit"
                disabled={isPending}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-on-primary text-sm font-semibold hover:bg-primary-hover disabled:opacity-60 transition-colors shadow-sm"
              >
                {isPending ? (
                  <><Loader2 size={15} className="animate-spin" /> Guardando...</>
                ) : (
                  <><Save size={15} /> {isEditing ? 'Guardar cambios' : 'Crear Pedido'}</>
                )}
              </button>

              {/* Errors summary: cada error con su ubicación, también los del servidor */}
              <ResumenDeErrores errores={[
                ...aplanarErroresDelFormulario(errors).filter(e => !erroresDelServidor.some(s => s.ruta === e.ruta)),
                ...erroresDelServidor,
              ]} />
            </div>
          </aside>
        </div>

        {/* Mobile save button */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-surface border-t border-divider p-3 shadow-lg z-30">
          <button
            type="submit"
            disabled={isPending}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-on-primary text-sm font-semibold hover:bg-primary-hover disabled:opacity-60 transition-colors"
          >
            {isPending ? (
              <><Loader2 size={15} className="animate-spin" /> Guardando...</>
            ) : (
              <><Save size={15} /> {isEditing ? 'Guardar cambios' : 'Crear Pedido'}</>
            )}
          </button>
        </div>
      </form>

      <CustomerModal
        open={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        onSubmit={handleCreateCustomer}
        isPending={createCustomer.isPending}
        taxIdLabel={taxIdLabel}
        serverErrors={customerErrors}
      />

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}
